import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@components/PageHeader';
import { Card } from '@components/ui/Card';
import { Button } from '@components/ui/Button';
import { Spinner } from '@components/ui/Spinner';
import { useEstateStore } from '../../stores/estateStore';
import { fetchPublicEstates, BlocPlusEstate } from '@services/blocPlusApi';
import { Classification, EstateItem } from '@types';

const AUSTRALIA_CENTER = { lat: -28.0, lng: 134.0 };
const DEFAULT_ZOOM = 5;

/** Stable key for an estate — prefer id, fall back to name */
function estateKey(estate: BlocPlusEstate): string {
  return estate.id || estate.name;
}

export const BlocPlusBrowse = () => {
  const navigate = useNavigate();
  const { loadEstateWithItems } = useEstateStore();

  const [estates, setEstates] = useState<BlocPlusEstate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState('');

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  // Sorted and filtered estates (never mutate state)
  const sortedEstates = useMemo(() => {
    const filtered = searchFilter
      ? estates.filter((e) => e.name.toLowerCase().includes(searchFilter.toLowerCase()))
      : estates;
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [estates, searchFilter]);

  const selectedEstate = useMemo(
    () => estates.find((e) => estateKey(e) === selectedKey) || null,
    [estates, selectedKey]
  );

  // Fetch estates from BlocPlus
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchPublicEstates();
        if (!cancelled) setEstates(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load estates');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  // Initialize map AFTER loading completes (so the div is in the DOM)
  useEffect(() => {
    if (loading) return;
    if (mapInstance.current) return; // already initialised
    if (!mapRef.current || typeof window.google === 'undefined') return;

    mapInstance.current = new google.maps.Map(mapRef.current, {
      center: AUSTRALIA_CENTER,
      zoom: DEFAULT_ZOOM,
      mapTypeId: 'roadmap',
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
    });

    infoWindowRef.current = new google.maps.InfoWindow();
  }, [loading]);

  // Draw markers when estates load and map is ready
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || estates.length === 0) return;

    // Clear old markers
    markersRef.current.forEach((m) => {
      google.maps.event.clearInstanceListeners(m);
      m.setMap(null);
    });
    markersRef.current = [];

    const bounds = new google.maps.LatLngBounds();

    estates.forEach((estate) => {
      if (!estate.geoLocation) return;

      const key = estateKey(estate);

      const marker = new google.maps.Marker({
        position: estate.geoLocation,
        map,
        title: estate.name,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: estate.status?.code === 'active' ? '#2CBB65' : '#9E9E9E',
          fillOpacity: 0.9,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
      });

      bounds.extend(estate.geoLocation);
      markersRef.current.push(marker);

      google.maps.event.addListener(marker, 'click', () => {
        setSelectedKey(key);
        map.panTo(estate.geoLocation);
        if (map.getZoom()! < 10) map.setZoom(10);

        infoWindowRef.current?.setContent(
          `<div style="font-size:13px;line-height:1.4;min-width:140px;">
            <strong>${estate.name}</strong><br/>
            <span style="color:#666;">${estate.availableLotsCount || 0} / ${estate.totalLotsCount || 0} lots available</span>
          </div>`
        );
        infoWindowRef.current?.open(map, marker);
      });
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, 60);
    }
  }, [estates, loading]); // re-run after map is initialised

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      markersRef.current.forEach((m) => {
        google.maps.event.clearInstanceListeners(m);
        m.setMap(null);
      });
      markersRef.current = [];
    };
  }, []);

  const handleImport = useCallback(
    (estate: BlocPlusEstate) => {
      const lotItems: EstateItem[] = (estate.lots || []).map((lot) => ({
        code: lot.code,
        handleid: lot.handleId || lot.id,
        landsize: lot.landSize || 0,
        frontage: lot.frontage || 0,
        price: parseFloat(lot.price) || 0,
        status: lot.status?.code || 'available',
        points: lot.points || [],
        classification: 'lot' as Classification,
      }));

      const refItems: EstateItem[] = (estate.references || []).map((ref, i) => ({
        code: ref.name,
        handleid: ref.handleId || `ref-${i}-${ref.name}`,
        landsize: 0,
        frontage: 0,
        price: 0,
        status: '',
        points: ref.points || [],
        classification: (ref.type?.code || 'road') as Classification,
      }));

      loadEstateWithItems(
        {
          planNumber: estate.name,
          name: estate.name,
          stage: '',
          address: '',
          status: estate.status?.code || 'active',
          geolocation: estate.geoLocation || { lat: -37.5, lng: 145.5 },
        },
        [...lotItems, ...refItems]
      );

      navigate(`/edit/${encodeURIComponent(estate.name)}`);
    },
    [loadEstateWithItems, navigate]
  );

  const handleSelectEstate = useCallback(
    (estate: BlocPlusEstate) => {
      const key = estateKey(estate);
      setSelectedKey(key);
      const map = mapInstance.current;
      if (map && estate.geoLocation) {
        map.panTo(estate.geoLocation);
        if (map.getZoom()! < 10) map.setZoom(10);
      }
    },
    []
  );

  return (
    <div>
      <PageHeader
        title="Browse BlocPlus"
        subtitle={loading ? 'Loading estates...' : `${estates.length} estates found`}
        showBackButton
      />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner />
          <span className="ml-3 text-sm text-gray-600">Loading estates from BlocPlus...</span>
        </div>
      ) : error ? (
        <Card>
          <div className="py-8 text-center">
            <p className="mb-4 text-sm text-red-600">{error}</p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Map */}
          <Card>
            <h3 className="mb-4 text-xl font-bold text-gray-800">Estate Map</h3>
            <div
              ref={mapRef}
              className="h-[500px] rounded-lg"
              style={{ width: '100%', minHeight: '400px' }}
            />
            <p className="mt-2 text-xs text-gray-500">
              Click a pin to view estate details
            </p>
          </Card>

          {/* Right panel */}
          <div className="space-y-6">
            {/* Selected estate detail */}
            {selectedEstate && (
              <Card>
                <div className="flex items-start justify-between">
                  <h3 className="text-xl font-bold text-gray-800">{selectedEstate.name}</h3>
                  <button
                    onClick={() => setSelectedKey(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    &times;
                  </button>
                </div>
                <div className="mt-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="rounded-full px-2 py-0.5 text-xs font-bold text-white"
                      style={{
                        backgroundColor:
                          selectedEstate.status?.code === 'active' ? '#2CBB65' : '#9E9E9E',
                      }}
                    >
                      {selectedEstate.status?.code || 'unknown'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs font-bold uppercase text-gray-700">Total Lots</p>
                      <p className="text-gray-600">
                        {selectedEstate.totalLotsCount || selectedEstate.lots?.length || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase text-gray-700">Available</p>
                      <p className="font-bold text-green-600">
                        {selectedEstate.availableLotsCount || 0}
                      </p>
                    </div>
                  </div>
                  {selectedEstate.lots && selectedEstate.lots.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase text-gray-700">Price Range</p>
                      <p className="text-sm text-gray-600">
                        {(() => {
                          const prices = selectedEstate.lots
                            .map((l) => parseFloat(l.price))
                            .filter((p) => !isNaN(p) && p > 0);
                          if (prices.length === 0) return 'N/A';
                          const min = Math.min(...prices);
                          const max = Math.max(...prices);
                          return min === max
                            ? `$${min.toLocaleString()}`
                            : `$${min.toLocaleString()} – $${max.toLocaleString()}`;
                        })()}
                      </p>
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-3">
                    <Button onClick={() => handleImport(selectedEstate)}>
                      Import to EstateMate
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Estate list */}
            <Card>
              <h3 className="mb-4 text-xl font-bold text-gray-800">
                Estates ({sortedEstates.length}{searchFilter ? ` of ${estates.length}` : ''})
              </h3>
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Filter by name..."
                className="mb-3 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <div className="max-h-96 space-y-2 overflow-y-auto">
                {sortedEstates.map((estate) => {
                  const key = estateKey(estate);
                  return (
                    <div
                      key={key}
                      onClick={() => handleSelectEstate(estate)}
                      className={`flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50 ${
                        key === selectedKey
                          ? 'border-yellow-400 bg-yellow-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <div>
                        <p className="text-s font-bold text-gray-800">{estate.name}</p>
                        <p className="text-xs text-gray-500">
                          {estate.availableLotsCount || 0} / {estate.totalLotsCount || 0} lots
                          available
                        </p>
                      </div>
                      <span
                        className="rounded-full px-2 py-0.5 text-xs font-bold text-white"
                        style={{
                          backgroundColor:
                            estate.status?.code === 'active' ? '#2CBB65' : '#9E9E9E',
                        }}
                      >
                        {estate.status?.code || '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};
